import { mutations, queries } from "@/graphql";
import { useMutation } from "@apollo/client/react";
import { useCallback, useContext } from "react";
import { enqueueSnackbar } from "notistack";
import { useTextDomainContext } from "../../app/providers";

interface UseReadNotificationReturn {
  readNotification: (_variables: Record<string, any>) => void;
  reading: boolean;
}

/**
 * Custom hook to read a notification.
 * @param {Function} [onCompleted] - Callback function to be called when the mutation completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 * @param {Array} [customRefetchQueries] - Optional custom refetch queries.
 */
export function useReadNotification(
  onCompleted: (_data: any) => void = () => {},
  onError: (_err: any) => void = () => {},
  customRefetchQueries?: any[],
): UseReadNotificationReturn {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const [readNotification, { loading }] = useMutation<any>(
    mutations.readNotification,
    {
      onCompleted: (_data) => {
        onCompleted(_data);

        enqueueSnackbar(gettext("Notification marked as read"), {
          variant: "success",
        });
      },
      onError: (_err) => {
        onError(_err);

        enqueueSnackbar("Read Notification: " + _err.message, {
          variant: "error",
        });
      },
      update: (cache, _result, { variables }) => {
        if (variables?.notificationId) {
          cache.modify({
            id: cache.identify({
              __typename: "NotificationType",
              id: variables.notificationId,
            }),
            fields: {
              Read() {
                return true;
              },
            },
          });
        }
      },
      refetchQueries: customRefetchQueries ?? [queries.getNewNotifications],
    },
  );

  const readNotificationRow = useCallback(
    (_variables: Record<string, any>) => {
      readNotification({
        variables: _variables,
      }).then();
    },
    [readNotification],
  );

  return {
    readNotification: readNotificationRow,
    reading: loading,
  };
}
